/**
 * The hand-off across a Stripe Checkout redirect (SOL-204).
 *
 * A Memoir is created by the webhook once payment SETTLES, not when Checkout
 * returns the buyer to us — so the home page cannot simply look for "the new
 * Memoir" on arrival; it may not exist for a few more seconds. Before leaving
 * for Checkout we record which Memoirs already existed; on return the home
 * polls until one appears that was not in that set, and opens it.
 *
 * sessionStorage, not localStorage: it belongs to this tab's purchase and
 * should not outlive it. Every access is guarded — storage can be blocked or
 * throw, and the purchase must still work, merely without the auto-open.
 */
const KEY = 'memoar_pending_purchase'

/** Query parameter Checkout's success_url carries back to the home page. */
export const PURCHASE_RETURN_PARAM = 'purchase'

export interface PendingPurchase {
  knownMemoirIds: string[]
  startedAt: number
}

export function rememberPendingPurchase(knownMemoirIds: string[]): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ knownMemoirIds, startedAt: Date.now() } satisfies PendingPurchase))
  } catch {
    // Without it the home still shows the new Memoir once it lands; it just
    // cannot tell which one to open.
  }
}

export function readPendingPurchase(): PendingPurchase | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingPurchase
    return Array.isArray(parsed.knownMemoirIds) ? parsed : null
  } catch {
    return null
  }
}

export function clearPendingPurchase(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    // nothing to do
  }
}

/** Where Checkout sends the buyer back to, on success and on cancel. */
export function purchaseReturnUrls(origin: string): { successUrl: string; cancelUrl: string } {
  return {
    successUrl: `${origin}/?${PURCHASE_RETURN_PARAM}=pending`,
    cancelUrl: `${origin}/`,
  }
}
